/* eslint-disable no-console */

import mixpanel, { Mixpanel } from 'mixpanel-browser';

import { AccountType } from '@constants/generic';
import { STORAGE_KEYS } from '@constants/storage';
import { store } from '@core/store';
import { getLocalStorageItem } from '@utils/storage';

import {
  BusinessNameSampleFlow,
  Event,
  KycDetailsFlowContext,
  MixpanelEventV2
} from './events';

import packageJson from '../../../../package.json';

const appVersion = packageJson.version;

/** `source` on Mixpanel payloads from this web client (matches other `sendMixpanelEvent` usage). */
export const MIXPANEL_WEB_SOURCE = 'Web';

const MIXPANEL_V2_INSTANCE_NAME = 'v2';
const MIXPANEL_V2_MODULE = 'web';

const accountTypeDisplayMap: Record<string, string> = {
  [AccountType.INDIVIDUAL]: 'Individual',
  [AccountType.ORGANIZATION]: 'Organization'
};

let mixpanelV2: Mixpanel | undefined;

// To initialize the mixpanel instance
export const initMixpanel = () => {
  const token = process.env.REACT_APP_PUBLIC_MIXPANEL_TOKEN;

  if (token && token.trim() !== '') {
    try {
      mixpanel.init(token, {
        ignore_dnt: true,
        track_pageview: false
      });
    } catch (error) {
      console.warn('Mixpanel initialization failed:', error);
    }
  }

  const tokenV2 = process.env.REACT_APP_PUBLIC_MIXPANEL_TOKEN_V2;

  if (tokenV2 && tokenV2.trim() !== '') {
    try {
      mixpanelV2 = mixpanel.init(
        tokenV2,
        { ignore_dnt: true, track_pageview: false },
        MIXPANEL_V2_INSTANCE_NAME
      );
    } catch (error) {
      console.warn('Mixpanel V2 initialization failed:', error);
    }
  }
};

// The `user`/`trial` slices in the real RootState are still loosely typed at
// their source, so this narrows just the fields the mixpanel module reads.
interface MixpanelAnalyticsState {
  user?: {
    currentUser?: {
      is_internal?: boolean;
      user_id?: string;
      mobile?: string;
      name?: string;
    };
    currentOrg?: {
      id?: string;
      name?: string;
      type?: string;
      role_name?: string;
      city?: string;
      state?: string;
      country?: string;
    };
    accountType?: string;
    rolesAndPermissions?: { role_name?: string };
    currentSubscription?: { plan_name?: string; payer_limit?: number };
  };
  trial?: { isTrialMode?: boolean };
}

const getAnalyticsState = (): MixpanelAnalyticsState =>
  store.getState() as unknown as MixpanelAnalyticsState;

const isInternalUser = (): boolean => {
  try {
    return Boolean(getAnalyticsState().user?.currentUser?.is_internal);
  } catch (e) {
    return false;
  }
};

const isTrialModeActive = (): boolean => {
  try {
    return Boolean(getAnalyticsState().trial?.isTrialMode);
  } catch (e) {
    return false;
  }
};

// Shared gate for sendMixpanelEvent / sendMixpanelEventV2 / trackPageView:
// suppress trial accounts, and internal users unless explicitly allowed.
const shouldSuppressTracking = (): boolean => {
  if (isTrialModeActive()) return true;

  const allowInternalEvents =
    process.env.REACT_APP_MIXPANEL_INTERNAL_EVENTS === 'true';
  return isInternalUser() && !allowInternalEvents;
};

// To track mixpanel event
export const sendMixpanelEvent = (
  event: string,
  data?: Record<string, unknown>
) => {
  if (
    !process.env.REACT_APP_PUBLIC_MIXPANEL_TOKEN ||
    process.env.REACT_APP_PUBLIC_MIXPANEL_TOKEN.trim() === ''
  ) {
    return;
  }

  if (shouldSuppressTracking()) return;

  try {
    mixpanel.track(event, { ...data, app_version: appVersion });
  } catch (error) {
    console.warn('Mixpanel event tracking failed:', error);
  }
};

type MixpanelUserState = NonNullable<MixpanelAnalyticsState['user']>;

// Properties describing the acting user (only set when known).
const getUserIdentityProperties = (
  currentUser: MixpanelUserState['currentUser'],
  userId: string,
  mobile?: string
): Record<string, unknown> => {
  const properties: Record<string, unknown> = {};
  if (userId) properties.user_internal = Boolean(currentUser?.is_internal);
  if (currentUser?.name) properties.user_name = currentUser.name;
  if (mobile) properties.user_phone_number = mobile;
  return properties;
};

// Properties describing the acting user's organization.
const getOrgProperties = (
  currentOrg: MixpanelUserState['currentOrg'],
  rolesAndPermissions: MixpanelUserState['rolesAndPermissions']
): Record<string, unknown> => {
  const properties: Record<string, unknown> = {
    org_name: currentOrg?.name || '',
    org_id:
      currentOrg?.id || getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID) || '',
    user_role: rolesAndPermissions?.role_name || currentOrg?.role_name || ''
  };
  if (currentOrg?.city) properties.org_city = currentOrg.city;
  if (currentOrg?.state) properties.org_state = currentOrg.state;
  if (currentOrg?.country) properties.org_country = currentOrg.country;
  return properties;
};

// Common properties attached to every event sent via the V2 mixpanel instance
const getCommonPropertiesV2 = (): Record<string, unknown> => {
  const { user } = getAnalyticsState();
  const { currentUser, currentOrg, currentSubscription } = user || {};

  const userId =
    currentUser?.user_id || getLocalStorageItem(STORAGE_KEYS.USER_ID) || '';
  const mobile =
    currentUser?.mobile || getLocalStorageItem(STORAGE_KEYS.MOBILE);
  const accountType = user?.accountType || currentOrg?.type || '';

  return {
    module: MIXPANEL_V2_MODULE,
    account_type: accountTypeDisplayMap[accountType] || '',
    subscription_plan: currentSubscription?.plan_name || '',
    user_band: currentSubscription?.payer_limit ?? '',
    user_id: userId,
    app_version: appVersion,
    ...getUserIdentityProperties(currentUser, userId, mobile),
    ...getOrgProperties(currentOrg, user?.rolesAndPermissions)
  };
};

// To track an event using the second (V2) mixpanel instance
export const sendMixpanelEventV2 = (
  event: string,
  data?: Record<string, unknown>
) => {
  const tokenV2 = process.env.REACT_APP_PUBLIC_MIXPANEL_TOKEN_V2;
  if (!tokenV2 || tokenV2.trim() === '' || !mixpanelV2) {
    return;
  }

  if (shouldSuppressTracking()) return;

  try {
    mixpanelV2.track(event, { ...getCommonPropertiesV2(), ...data });
  } catch (error) {
    console.warn('Mixpanel V2 event tracking failed:', error);
  }
};

export const trackPageView = (pageName: string, data = {}) => {
  if (
    !process.env.REACT_APP_PUBLIC_MIXPANEL_TOKEN ||
    process.env.REACT_APP_PUBLIC_MIXPANEL_TOKEN.trim() === ''
  ) {
    return;
  }

  if (shouldSuppressTracking()) return;

  try {
    mixpanel.track_pageview({
      page: pageName,
      ...data,
      app_version: appVersion
    });
  } catch (error) {
    console.warn('Mixpanel page view tracking failed:', error);
  }
};

export const setUserProperties = (
  identity: string,
  userProperties: Record<string, unknown>
) => {
  if (
    !process.env.REACT_APP_PUBLIC_MIXPANEL_TOKEN ||
    process.env.REACT_APP_PUBLIC_MIXPANEL_TOKEN.trim() === ''
  ) {
    return;
  }

  if (isTrialModeActive() || isInternalUser()) {
    return;
  }

  try {
    mixpanel.identify(identity);
    Object.entries(userProperties).forEach(([key, value]) => {
      mixpanel.people.set(key, value);
    });
  } catch (error) {
    console.warn('Mixpanel user properties setting failed:', error);
  }
};

export type LandingPageKycStatus =
  | 'pending_verification'
  | 'active'
  | 'active_pending_kyc'
  | 'active_read_only'
  | 'rejected'
  | 'resubmitted_pending_kyc';

export type { BusinessNameSampleFlow, KycDetailsFlowContext };
export { Event, MixpanelEventV2 };
